import { useEffect, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Minus, Plus } from 'lucide-react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { cn } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { ControlledNumberInput } from '@fortem-monorepo/react-ui'
import { FormError } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'
import { InputGroup, InputGroupAddon } from '@fortem-monorepo/react-ui'

import { AutocallStandardSchemaType } from '@/pages/instruments/new-instrument/schemas/autocall-standard.schema'
import { StrikeSchemaType } from '@/pages/instruments/new-instrument/schemas/shared/base-structured.schema'
import { useCacheInstruments, useInstrumentsByTypeQuery } from '@/react-query/queries/instruments'

type Schema = { underlyings: StrikeSchemaType[]; initial_observation_date: string }

export interface UnderlyingsFormFieldProps {
  maxUnderlyings?: number
}

export const UnderlyingsFormField: React.FC<UnderlyingsFormFieldProps> = ({ maxUnderlyings }) => {
  const { control, register, trigger, setValue } = useFormContext<Schema>()
  const { append, remove, fields } = useFieldArray<Schema>({ control, name: 'underlyings' })

  const api = useApi()

  const [underlyings, initial_observation_date] = useWatch({
    control,
    name: ['underlyings', 'initial_observation_date'],
  })

  const underlying_ids = (underlyings ?? [])
    .map((u) => {
      return u.underlying_id ? u.underlying_id : undefined
    })
    .filter((u) => u !== undefined)

  const cacheInstrument = useCacheInstruments()
  const { data, isFetching } = useInstrumentsByTypeQuery([1, 11])

  const spotQuery = useQuery({
    queryFn: () =>
      api.instruments.getUnderlyingPrices({
        underlying_ids,
        value_date: initial_observation_date,
      }),
    queryKey: [
      'instruments',
      'underlyings',
      'prices',
      { underlying_ids, value_date: initial_observation_date },
    ],
    enabled: underlying_ids.length > 0,
  })

  const priceMap = useMemo(() => {
    if (!spotQuery.data) return new Map<number, number>()

    return new Map(spotQuery.data.map((p) => [p.instrument_id, p.spot]))
  }, [spotQuery.data])

  useEffect(() => {
    if (!spotQuery.isFetching) {
      underlying_ids.forEach((id, index) => {
        setValue(`underlyings.${index}.value`, priceMap.get(id) ?? 100)
      })
    }
  }, [priceMap, spotQuery.isFetching])

  const options = useMemo(() => {
    return (data ?? []).map((item) => ({
      label: item.label,
      value: item.value?.toString(),
    }))
  }, [data])

  useEffect(() => {
    trigger()
  }, [underlyings])

  return (
    <FormField
      control={control}
      name="underlyings"
      render={() => {
        return (
          <FormItem>
            <div className={'space-y-2'}>
              <div className="flex gap-2 items-center">
                <FormLabel>Underlyings</FormLabel>
                <FormError />
              </div>
            </div>
            {fields?.map((_, index) => {
              return (
                <div className={'flex items-center gap-2'} key={index}>
                  <FormField
                    control={control}
                    name={`underlyings.${index}.underlying_id`}
                    render={({ field: { value } }) => {
                      return (
                        <SearchSelect
                          loading={isFetching || spotQuery.isFetching}
                          className={'min-w-0! flex-1'}
                          optionDisabled={(value) => {
                            return underlying_ids.includes(parseInt(value))
                          }}
                          disabled={isFetching || spotQuery.isFetching}
                          options={options}
                          placeholder={'Select an underlying'}
                          value={value?.toString()}
                          setValue={(value) => {
                            cacheInstrument([parseInt(value)])
                            setValue(`underlyings.${index}.underlying_id`, parseInt(value), {
                              shouldValidate: true,
                            })
                          }}
                        />
                      )
                    }}
                  />
                  <FormField
                    control={control}
                    name={`underlyings.${index}.value`}
                    render={({ fieldState: { invalid } }) => {
                      return (
                        <InputGroup className={'flex-1'}>
                          <ControlledNumberInput
                            thousandSeparator={','}
                            disabled={spotQuery.isFetching}
                            className={
                              'w-full flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent'
                            }
                            {...register(`underlyings.${index}.value`)}
                            placeholder={'Strike value'}
                          />
                          <InputGroupAddon align="inline-end">
                            <FormLabel
                              className={cn({
                                'text-muted-foreground font-normal': !invalid,
                              })}
                            >
                              Value
                            </FormLabel>
                          </InputGroupAddon>
                        </InputGroup>
                      )
                    }}
                  />

                  <FormField
                    control={control}
                    name={`underlyings.${index}.percent`}
                    render={({ fieldState: { invalid } }) => {
                      return (
                        <InputGroup className={' w-full flex-1'}>
                          <ControlledNumberInput
                            thousandSeparator={','}
                            allowNegative={false}
                            min={0}
                            max={100}
                            className={
                              'flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent'
                            }
                            {...register(`underlyings.${index}.percent`)}
                            placeholder={'Strike percent'}
                          />
                          <InputGroupAddon align="inline-end">
                            <FormLabel
                              className={cn({
                                'text-muted-foreground font-normal': !invalid,
                              })}
                            >
                              %
                            </FormLabel>
                          </InputGroupAddon>
                        </InputGroup>
                      )
                    }}
                  />

                  {index === 0 ? (
                    <Button
                      variant={'outline'}
                      disabled={fields.length >= (maxUnderlyings ?? Infinity)}
                      className={'flex-0'}
                      onClick={async () => {
                        append({
                          value: 100,
                          percent: 100,
                        } as AutocallStandardSchemaType['underlyings'][number])
                      }}
                    >
                      <Plus />
                    </Button>
                  ) : (
                    <Button
                      variant={'outline'}
                      className={'flex-0'}
                      onClick={async () => {
                        remove(index)
                      }}
                    >
                      <Minus />
                    </Button>
                  )}
                </div>
              )
            })}
          </FormItem>
        )
      }}
    />
  )
}
